import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { attendanceStatusDisplay } from "@/lib/attendance-entry"
import { extractErrorCode, extractErrorStatus, translateError } from "@/lib/error-messages"

// Règles du portail apprenant qui ne dépendent pas de React : forme d'une
// séance à émarger, inscriptions émargeables ou en cours, message d'un refus
// de signature.

/**
 * Séance à émarger — décodée de `PortalAttendanceRecord` (`learner_portal/presence.rs`).
 * Une séance sans ligne d'émargement porte `status = "pending"` et
 * `id = sheet_id`. La signature se fait sur `sheet_id`.
 */
export interface PortalAttendanceRecord {
  id: string
  sheet_id: string
  slot_date: string
  slot_start_time: O.Option<string>
  slot_end_time: O.Option<string>
  title: O.Option<string>
  status: string
  status_label: string
  signed_at: O.Option<string>
  signature_url: O.Option<string>
  /** Notes du stagiaire sur sa présence : jamais le nom du formateur. */
  notes: O.Option<string>
  /** Demi-journée de la feuille : deux séances du même jour se distinguent par elle. */
  slot_period: O.Option<SlotPeriodCode>
  /** Lieu de la session, `none` à distance ou non renseigné. */
  location: O.Option<string>
  instructor_name: O.Option<string>
  /** Calculé par le serveur : non signée, séance du jour, feuille non finalisée. */
  can_sign: boolean
  /** Pourquoi la séance ne se signe pas (`none` quand elle se signe). */
  sign_unavailable_reason: O.Option<SignUnavailableReason>
}

export type SlotPeriodCode = "am" | "pm" | "full"

export type SignUnavailableReason = "finalized" | "wrong_day" | "already_signed" | "inactive_registration"

const SLOT_PERIOD_LABELS: Record<SlotPeriodCode, string> = {
  am: "Matin",
  pm: "Après-midi",
  full: "Journée",
}

const SIGN_UNAVAILABLE_MESSAGES: Record<SignUnavailableReason, string> = {
  finalized: "Feuille finalisée : elle n'accepte plus de signature.",
  wrong_day: "Signature possible uniquement le jour de la séance.",
  already_signed: "Séance déjà signée.",
  inactive_registration: "Inscription non active : la signature n'est pas possible.",
}

function isSlotPeriodCode(value: string): value is SlotPeriodCode {
  return value in SLOT_PERIOD_LABELS
}

function isSignUnavailableReason(value: string): value is SignUnavailableReason {
  return value in SIGN_UNAVAILABLE_MESSAGES
}

function text(raw: Record<string, unknown>, key: string): O.Option<string> {
  return pipe(
    O.fromNullable(raw[key]),
    O.filter((value): value is string => typeof value === "string"),
  )
}

/** Décode une séance de l'API : les champs absents deviennent `none` au point d'entrée. */
export function decodePortalAttendanceRecord(raw: Record<string, unknown>): PortalAttendanceRecord {
  const required = (key: string) =>
    pipe(
      text(raw, key),
      O.getOrElse(() => ""),
    )
  return {
    id: required("id"),
    sheet_id: required("sheet_id"),
    slot_date: required("slot_date"),
    slot_start_time: text(raw, "slot_start_time"),
    slot_end_time: text(raw, "slot_end_time"),
    title: text(raw, "title"),
    status: required("status"),
    status_label: required("status_label"),
    signed_at: text(raw, "signed_at"),
    signature_url: text(raw, "signature_url"),
    notes: text(raw, "notes"),
    slot_period: pipe(text(raw, "slot_period_code"), O.filter(isSlotPeriodCode)),
    location: pipe(text(raw, "location"), O.filter((value) => value.trim().length > 0)),
    instructor_name: pipe(text(raw, "instructor_name"), O.filter((value) => value.trim().length > 0)),
    can_sign: raw.can_sign === true,
    sign_unavailable_reason: pipe(text(raw, "sign_unavailable_reason"), O.filter(isSignUnavailableReason)),
  }
}

/** « Matin », « Après-midi », « Journée » ; vide sans demi-journée connue. */
export function portalSlotPeriodLabel(slot: Pick<PortalAttendanceRecord, "slot_period">): string {
  return pipe(
    slot.slot_period,
    O.match(
      () => "",
      (code) => SLOT_PERIOD_LABELS[code],
    ),
  )
}

/** Motif affiché à la place du bouton « Signer » ; un motif inconnu garde un texte générique. */
export function signUnavailableMessage(slot: Pick<PortalAttendanceRecord, "sign_unavailable_reason">): string {
  return pipe(
    slot.sign_unavailable_reason,
    O.match(
      () => "Signature non disponible.",
      (reason) => SIGN_UNAVAILABLE_MESSAGES[reason],
    ),
  )
}

/** « 87 % » ; « non disponible » tant qu'aucune feuille d'émargement n'est finalisée. */
export function formatAttendanceRate(rate: O.Option<number>): string {
  return pipe(
    rate,
    O.match(
      () => "non disponible",
      (value) => `${Math.round(value)}\u00a0%`,
    ),
  )
}

/** Valeur d'une barre de progression : bornée à [0, 100], `none` sans taux. */
export function attendanceRateBarValue(rate: O.Option<number>): O.Option<number> {
  return pipe(
    rate,
    O.map((value) => Math.min(100, Math.max(0, value))),
  )
}

/** Taux de présence de l'API : un nombre fini, sinon `none` (aucune feuille finalisée). */
export function decodeAttendanceRate(raw: unknown): O.Option<number> {
  return pipe(
    O.fromNullable(raw),
    O.filter((rate): rate is number => typeof rate === "number" && Number.isFinite(rate)),
  )
}

/** Formation suivie jusqu'au bout : elle ne s'affiche plus « en cours ». */
const FINISHED_STATUSES: ReadonlySet<string> = new Set(["attended", "completed", "certified"])

/** Inscription telle que le portail la reçoit : statut et drapeau serveur. */
export interface PortalRegistrationState {
  status: string
  /** `registration_status_is_active` côté serveur : seule définition d'une inscription active, le portail ne la recalcule pas. */
  is_active: boolean
}

/** Inscription émargeable (active au sens du serveur) : la page d'émargement la propose. */
export function isAttendanceRegistration(registration: PortalRegistrationState): boolean {
  return registration.is_active
}

/** Formation en cours : émargeable et pas encore terminée. */
export function isOngoingRegistration(registration: PortalRegistrationState): boolean {
  return isAttendanceRegistration(registration) && !FINISHED_STATUSES.has(registration.status)
}

export function isFinishedRegistration(status: string): boolean {
  return FINISHED_STATUSES.has(status)
}

/** « de 09:00 à 12:30 » ; vide quand la feuille n'a pas d'horaires prévus. */
export function portalSlotHours(slot: Pick<PortalAttendanceRecord, "slot_start_time" | "slot_end_time">): string {
  return pipe(
    O.Do,
    O.apS("from", slot.slot_start_time),
    O.apS("to", slot.slot_end_time),
    O.match(
      () => "",
      ({ from, to }) => `de ${from.slice(0, 5)} à ${to.slice(0, 5)}`,
    ),
  )
}

/** Intitulé d'une séance : celui du créneau, « Séance » à défaut. */
export function portalSlotTitle(slot: Pick<PortalAttendanceRecord, "title">): string {
  return pipe(
    slot.title,
    O.filter((title) => title.trim().length > 0),
    O.getOrElse(() => "Séance"),
  )
}

/**
 * Refus définitif d'une signature : le serveur a répondu par un refus (4xx :
 * feuille finalisée, séance d'un autre jour, séance inconnue). Réessayer n'y
 * changera rien ; seuls la panne et la limite de débit se réessaient.
 */
export function isDefinitiveSignRefusal(error: unknown): boolean {
  return pipe(
    O.fromNullable(extractErrorStatus(error)),
    O.exists((status) => status >= 400 && status < 500 && status !== 408 && status !== 429),
  )
}

/** Message d'un échec de signature : jamais « réessayez » pour un refus définitif. */
export function portalSignErrorMessage(error: unknown): string {
  if (extractErrorCode(error) === "ATTENDANCE_RECORDS_LOCKED") {
    return "La feuille d'émargement de cette séance est finalisée : elle n'accepte plus de signature. Adressez-vous à votre organisme de formation."
  }
  if (isDefinitiveSignRefusal(error)) return translateError(error)
  return "Impossible d'enregistrer votre signature. Vérifiez votre connexion et réessayez."
}

/** « 3 séances finalisées » ; « Aucune séance finalisée » avant la première. */
export function finalizedSheetsLabel(count: number): string {
  if (count === 0) return "Aucune séance finalisée"
  return count === 1 ? "1 séance finalisée" : `${count} séances finalisées`
}

/**
 * Badge d'une séance du portail : « En attente » tant qu'elle n'est pas
 * émargée (`pending`), sinon le libellé de `ref_attendance_status` partagé
 * avec le back-office (jamais une table locale aux codes inventés).
 */
export function portalAttendanceBadge(status: string): ReturnType<typeof attendanceStatusDisplay> {
  if (status === "pending") return { label: "En attente", variant: "outline" }
  return attendanceStatusDisplay(status)
}
